import { RootState } from "..";
import {
    SET_CONNECTION_STATUS,
    SetConnectionStatusAction,
    ServerToClientMessage,
    ConnectionStatus,
    LoadTemplateClientToServerMessage,
    LoadComponentDocumentationClientToServerMessage,
    LoadCanvasDataClientToServerMessage,
    LoadCssClassesClientToServerMessage,
    StorylineLoadedClientToServerMessage,
    PageLoadedClientToServerMessage,
    TemplateRenderedClientToServerMessage,
} from "./types";
import {
    UPDATE_CURRENT_SLIDE_TEMPLATE,
    UPDATE_SLIDE_TEMPLATES,
    UpdateCurrentSlideTemplate,
    UpdateSlideTemplates
} from "../storyline/types";
import type { DocumentedComponent } from "../../shared/components/DocumentedComponent";
import { saveCurrentPageTemplates } from "../storyline/actions";
import * as _ from "lodash";

export type ConnectionSettings = {
    url: string
}

const defaultConfig: ConnectionSettings = {
    url: "ws://127.0.0.1:11223"
};

let connection: WebSocket;

function setupConnection(connection: WebSocket, dispatch: any) {
    connection.onmessage = ((event: MessageEvent<string>) => {
        const message = JSON.parse(event.data) as ServerToClientMessage;
        switch (message.type) {
            case "REQUEST_TEMPLATE":
                dispatch(sendCurrentTemplateToVsCode());
                return;

            case "REQUEST_TEMPLATES":
                dispatch(sendStorylineTemplatesToVsCode());
                return;

            case "SAVE_TEMPLATES":
                dispatch(saveCurrentPageTemplates(message.templates));
                return;

            case "UPDATE_TEMPLATE":
                dispatch({
                    type: UPDATE_CURRENT_SLIDE_TEMPLATE,
                    newTemplate: message.template,
                    newCustomCss: message.css
                } as UpdateCurrentSlideTemplate);
                return;

            case "UPDATE_TEMPLATES":
                dispatch({
                    type: UPDATE_SLIDE_TEMPLATES,
                    newTemplates: message.templates
                } as UpdateSlideTemplates);
        }
    });
}

function setConnectionStatus(status: ConnectionStatus) {
    return {
        type: SET_CONNECTION_STATUS,
        status
    } as SetConnectionStatusAction;
}

export function openWsConnection(availableComponents) {
    return async (dispatch) => {
        connection = new WebSocket(defaultConfig.url);
        setupConnection(connection, dispatch);

        connection.onopen = (e) => {
            dispatch(setConnectionStatus("OPEN"));
            sendCurrentComponentDocumentationToVsCode(availableComponents);
            dispatch(sendStorylineStateToVsCode());
            dispatch(sendCanvasDataToVsCode());
        }
        connection.onerror = (e) => {
            console.error(e);
            dispatch(setConnectionStatus("ERROR"));
        };

        connection.onclose = (e) => {
            dispatch(setConnectionStatus("CLOSED"));
        }
    }
}

export function closeWsConnection() {
    return async (dispatch, _getState: () => RootState) => {
        dispatch(setConnectionStatus("CLOSED"));
    }
}

export function sendCurrentTemplateToVsCode() {
    return async (_dispatch, getState: () => RootState) => {
        const currentTemplate = getState()?.storyline?.currentParagraph?.template;
        if (!currentTemplate) return;

        if (connection?.readyState === WebSocket.OPEN) {
            connection.send(JSON.stringify({
                type: "LOAD_TEMPLATE",
                template: currentTemplate.jsx,
                css: currentTemplate.css
            } as LoadTemplateClientToServerMessage));
        }
    }
}

export function sendStorylineTemplatesToVsCode() {
    return async (_dispatch, getState: () => RootState) => {
        const templates = getState()?.storyline?.storyline?.templates;
        if (!templates) return;

        if (connection?.readyState === WebSocket.OPEN) {
            connection.send(JSON.stringify({
                type: "LOAD_TEMPLATES",
                templates
            }));
        }
    }
}

export function sendCurrentComponentDocumentationToVsCode(availableComponents) {
    const componentDocumentationEntries = Object.entries(availableComponents)
        .map(([name, component]: [string, DocumentedComponent]) => [name, component?.metadata ?? { attributes: [] }]);

    if (connection?.readyState === WebSocket.OPEN) {
        connection.send(JSON.stringify({
            type: "LOAD_COMPONENT_DOCUMENTATION",
            components: Object.fromEntries(componentDocumentationEntries)
        } as LoadComponentDocumentationClientToServerMessage));
    }
}

type CanvasBindingKind = "canvasState" | "frameData" | "parameterValue" | "function" | "library" | "metadata";

/**
 * Additional metadata related to a canvas binding field.  Used by the VS Code extension for tooltips + suggestions.
 * 
 * @property kind - The category of the field.
 * @property description - A user-friendly description for the field.
 * @property signature - The (optional) signature of the field.  Only applicable to values which can be invoked (functions, constructors, etc.).
 * @property template - The (optional) autocomplete template of the field.  Only applicable to values which can be invoked (functions, constructors, etc.).
 */
type CanvasBindingMetadata = {
    kind: CanvasBindingKind,
    description?: string,
    signature?: string,
    template?: string,
};

const FUNCTIONS_DOCUMENTATION: Record<string, CanvasBindingMetadata> = {
    formatNumber: {
        kind: "function",
        signature: `function formatNumber(value: number, decimalPlaces = 2, prefix = "", suffix = "", multiplicationFactor = 1)`,
        description: "Formats a number to the given specification.",
        template: `formatNumber($1, $2)`
    }, formatDate: {
        kind: "function",
        signature: `function formatDate(value: string | Date | moment.Moment, formatString = "YYYY-MM-DD HH:mm")`,
        description: "Formats a date to the given specification.",
        template: `formatDate($1, $2)`
    }, updateParameterValue: {
        kind: "function",
        signature: `function updateParameterValue(parameterName: string, newValueOrTransformer: unknown | ((oldValue: unknown) => unknown), isInitialLoad = false, preventDatasourceRefresh = false, sourceDatasourceId: string = null)`,
        description: "Updates the value of a storyline parameter.\n\nIf the `newValueOrTransformer` argument is a function, it will be invoked with the current value of the specified parameter and the result will be used as the new value.  This alleviates the need for repeating the parameter name/path within the calculation logic.",
        template: `updateParameterValue("$1", $2)`
    }, updateParameterValueAction: {
        kind: "function",
        signature: "function updateParameterValueAction(parameterName: string, newValue: any, isInitialLoad = false, preventDatasourceRefresh = false, sourceDatasourceId: string = null)",
        description: "**DEPRECATED - PLEASE USE `updateParameterValue` INSTEAD.**\n\nUpdates the value of a storyline parameter.",
        template: `updateParameterValueAction("$1", $2)`
    }, setDefaultParameterValue: {
        kind: "function",
        signature: `function setDefaultParameterValue(parameterName: string, value: any)`,
        description: "Assigns the provided value to the specified parameter **if the current parameter value is `null` or `undefined`**.\n\nShorthand for `if (parameterValues.get(parameterName) != null) { updateParameterValue(parameterName, value); }`.",
        template: `setDefaultParameterValue("$1", $2)`
    }, applyParameterValueChanges: {
        kind: "function",
        signature: `function applyParameterValueChanges()`,
        description: "Triggers a manual refresh of all pending datasources which are 'dirty' due to parameter value changes.\n\nOnly applies to datasources which are explicitly excluded from automatic refreshes.",
        template: `applyParameterValueChanges()`
    }, applyParameterValueChangesAction: {
        kind: "function",
        signature: "function applyParameterValueChangesAction()",
        description: "**DEPRECATED - PLEASE USE `applyParameterValueChanges` INSTEAD.**\n\nTriggers a manual refresh of all pending datasources which are 'dirty' due to parameter value changes.\n\nOnly applies to datasources which are explicitly excluded from automatic refreshes.",
        template: `applyParameterValueChanges()`
    }, refreshDatasourceByName: {
        kind: "function",
        signature: `function refreshDatasourceByName(name: string, additionalParameters = {}, mapResultToDatasourceParameters = false, compressRequestBody = false)`,
        description: "Explicitly refreshes a datasource by name, even if no parameter value changes have occurred since the last refresh.\n\n`additionalParameters` can be used to augment/override the storyline parameters.  This takes the form of a standard object (dictionary), where the field names are treated as the parameter names.  This is useful in cases where input values are only used once-off or where the change tracker should be bypassed.\n\nIf `mapResultToDatasourceParameters` is `true`, any parameters with names matching a slot linked to this datasource will be updated with the newly retrieved value.\n\n`compressRequestBody` can be set to `true` to compress the request body using gzip compression, which should only be used for large size parameters.",
        template: `refreshDatasourceByName("$1", {}, true)`
    }, setDatasourceData: {
        kind: "function",
        signature: `function setDatasourceData(datasourceName: string, newValueOrTransformer: unknown | ((oldValue: unknown) => unknown))`,
        description: "Updates the current value of the specified datasource.\n\nIf the `newValueOrTransformer` argument is a function, it will be invoked with the current value of the specified datasource and the result will be used as the new value.  This alleviates the need for repeating the datasource name/path within the calculation logic.\n\nNOTE: If a datasource with the specified name does not exist, a new one is created.  However, this new datasource will not be linked to any slots within the storyline and can only be accessed using the `datasourceValues` object.",
        template: `setDatasourceData("$1", [{}])`
    }, patchDatasourceData: {
        kind: "function",
        signature: `function patchDatasourceData(datasourceName: string, diff: Object | Array | unknown)`,
        description: "Patches the current value of the specified datasource, using the provided diff object.\n\nIf the existing value is object-like (either an object or an array), Lodash's `merge` function is used to merge the diff object with the existing value.\n\nDatasource value changes are applied to all relevant slots within the storyline.\n\nNOTE: If a datasource with the specified name does not exist, a new one is created.  However, this new datasource will not be linked to any slots within the storyline and can only be accessed using the `datasourceValues` object.",
        template: `patchDatasourceData("$1", {})`
    }, fetchDatasourceByName: {
        kind: "function",
        signature: `function fetchDatasourceByName(name: string, additionalParameters = {}, compressRequestBody = false)`,
        description: "Fetches a datasource by name, returning the result and leaving the existing datasource value unchanged.\n\nIf the existing datasource value needs to be updated, use the `setDatasourceData` or `patchDatasourceData` functions.\n\n`additionalParameters` can be used to augment/override the storyline parameters.  This takes the form of a standard object (dictionary), where the field names are treated as the parameter names.\n\nThis function call returns a `Promise` containing the API response object or the error encountered.  Use the `then` and `error` callbacks of the promise to consume these values.\n\n`compressRequestBody` can be set to `true` to compress the request body using gzip compression, which should only be used for large size parameters.",
        template: `fetchDatasourceByName("$1", {})`
    }, showError: {
        kind: "function",
        signature: `function showError(message: string, options: NotificationOptions = {})`,
        description: "Displays a toast notification with the given message, in the style of an error message.",
        template: `showError("$1")`
    }, showSuccess: {
        kind: "function",
        signature: `function showSuccess(message: string, options: NotificationOptions = {})`,
        description: "Displays a toast notification with the given message, in the style of a success message.",
        template: `showSuccess("$1")`
    }, showWarning: {
        kind: "function",
        signature: `function showWarning(message: string, options: NotificationOptions = {})`,
        description: "Displays a toast notification with the given message, in the style of a warning message.",
        template: `showWarning("$1")`
    }, showInfo: {
        kind: "function",
        signature: `function showInfo(message: string, options: NotificationOptions = {})`,
        description: "Displays a toast notification with the given message, in the style of an info message.",
        template: `showInfo("$1")`
    }, showDetailedError: {
        kind: "function",
        signature: `function showDetailedError(message: string, details = "", options: NotificationOptions = {})`,
        description: "Displays a toast notification with the given message, in the style of a error message.\n\nThe `details` text is displayed inside an expandable area inside the message body.",
        template: `showDetailedError("$1", "$2")`
    }, goToID: {
        kind: "function",
        signature: `function goToID(id: string)`,
        description: "Immediately navigates to the first frame of the canvas with the given name/id.\n\nOnly applies to canvases within the current storyline.",
        template: `goToID("$1")`
    }, goToXYZ: {
        kind: "function",
        signature: `function goToXYZ(xIndex: number, yIndex: number, frameIndex: number)`,
        description: "Immediately navigates to the frame with the given coordinates.  X = chapter index, Y = page index, Z = frame index.",
        template: `goToXYZ($1, $2, $3)`
    }, clsx: {
        kind: "function",
        signature: `function clsx(...classes: ClassArray | ClassDictionary | string | number | null | boolean | undefined): string`,
        description: "Helper function for building up a dynamic class list string.\n\nTakes an arbitrary number of input arguments.\n\nEach argument is evaluated in turn and any 'falsey' values are discarded.\n\nSpace-separates the surviving class name list, for use in `className` bindings. See the [library documentation](https://github.com/lukeed/clsx#usage) for usage examples.",
        template: `clsx($1)`
    }, navigateToPage: {
        kind: "function",
        signature: `function navigateToPage(targetUrl: string, additionalParameters: Object = {}, openNewTab: boolean = false, obfuscateParameters: boolean = false, includeNavFilterValues: boolean = true): void`,
        description: "Navigates to the provided URL.\n\nBy default, all parameters contained within a `NavFilters` component is included in the query parameters, combined with the additional parameter values provided.",
        template: `navigateToPage($1)`
    }, trackEvent: {
        kind: "function",
        signature: `function trackEvent(event: { name: string }, customProperties?: Record<string, any>)`,
        description: "Logs a user action or other occurrence to App Insights.",
        template: `trackEvent({ name: "$1" })`
    }, trackException: {
        kind: "function",
        signature: `function trackException(error: { id?: string, exception?: Error, severityLevel?: number })`,
        description: "Logs a caught exception to App Insights.\n\nSeverity level can be one of the following values: Verbose = 0, Information = 1, Warning = 2, Error = 3, Critical = 4",
        template: `trackException({ exception: $1 })`
    }, trackTrace: {
        kind: "function",
        signature: `function trackTrace(trace: { message: string, severityLevel?: number }, customProperties?: Record<string, any>)`,
        description: "Logs a diagnostics message to App Insights.\n\nSeverity level can be one of the following values: Verbose = 0, Information = 1, Warning = 2, Error = 3, Critical = 4",
        template: `trackTrace({ message: "$1" })`
    }, trackMetric: {
        kind: "function",
        signature: `function trackMetric(metric: { name: string, average: number, sampleCount?: number, min?: number, max?: number }, customProperties?: Record<string, any>)`,
        description: "Logs a numeric value (which is associated with a specific event) to App Insights.\n\nTypically used to send regular reports of performance indicators.\n\nTo send a single measurement, just use the `name` and `average` fields.\n\nIf you take measurements frequently, you can reduce the telemetry bandwidth by aggregating multiple measurements and sending the resulting average and modifying the `sampleCount` field.",
        template: `trackMetric({ name: "$1", average: $2 })`
    }, switchBranch: {
        kind: "function",
        signature: "function switchBranch(branchName: string)",
        description: "Switches to the specified branch.\n\nAll application data will be reloaded from the relevant Metastore branch.",
        template: `switchBranch("$1")`
    }, showBanner: {
        kind: "function",
        signature: "function showBanner(banner: { severity: \"error\" | \"warning\" | \"info\" | \"success\", content: string })",
        description: "Shows a dismissable banner notification at the top of the page.\n\nIntended for persistent warnings or errors which the user must manually acknowledge/dismiss.\n\nThe `content` field supports any Markdown, including `qerent` elements.  This can be used for interactive elements which manipulate the current canvas state.",
        template: `showBanner({ severity: "info", content: "$2" })`
    }, dismissBanner: {
        kind: "function",
        signature: "function dismissBanner()",
        description: "Dismisses the current banner notification (if present).",
        template: `dismissBanner()`
    }, compareVersions: {
        kind: "function",
        signature: "function compareVersions(v1: string, v2: string, operator: CompareOperator): boolean",
        description: `Compare semver version strings using the specified operator.

@param v1 — First version to compare

@param v2 — Second version to compare

@param operator — Allowed arithmetic operator to use

@returns — true if the comparison between the firstVersion and the secondVersion satisfies the operator, false otherwise.

@see - https://github.com/omichelsen/compare-versions?tab=readme-ov-file#human-readable-compare

@example

\`\`\`
compare('10.1.8', '10.0.4', '>'); // return true
compare('10.0.1', '10.0.1', '='); // return true
compare('10.1.1', '10.2.2', '<'); // return true
compare('10.1.1', '10.2.2', '<='); // return true
compare('10.1.1', '10.2.2', '>='); // return false
\`\`\``,
        template: `compareVersions($1, "$2", ">=")`,
    },
};

function canvasDataSerializer(key: string, value: unknown): any {
    if (_.isElement(value) || _.isSymbol(value) || _.has(value, "$$typeof")) {
        return undefined;
    }

    if (typeof value === "function") {
        return value.toString()?.slice(0, 100);
    }

    if (key === "logger") {
        return null;
    }
    
    return value;
}

export function sendCanvasDataToVsCode() {
    return async (_dispatch, getState: () => RootState) => {
        if (connection?.readyState === WebSocket.OPEN) {
            const canvasData = {
                ...window["canvasBindings"],
                functions: FUNCTIONS_DOCUMENTATION
            };

            if (!canvasData) return;

            connection.send(JSON.stringify({
                type: "LOAD_CANVAS_DATA",
                canvasData
            } as LoadCanvasDataClientToServerMessage, canvasDataSerializer));
        }
    }
}

function getAllCssClassNamesForCurrentDocument() {
    var classNameGrabber = /\.([A-Za-z0-9\-_]*)(?:,|:|$|\s)+/g;

    var styleSheets = Array.from(document.styleSheets);
    var styleSheetRules = styleSheets
        .flatMap(ss =>
            Array.from(ss.cssRules).map(ssr => ssr["selectorText"]))
        .filter(ssr => ssr);
    var classNames = styleSheetRules.flatMap(ssr =>
        Array.from(ssr.matchAll(classNameGrabber)).map(m => m[1])
    );

    return _.uniq(classNames.filter(cn => !cn.startsWith("_") && !cn.startsWith("css")));
}

export function sendCssClassesToVsCode() {
    if (connection?.readyState === WebSocket.OPEN) {
        const classNames = getAllCssClassNamesForCurrentDocument();

        if (!classNames) return;

        connection.send(JSON.stringify({
            type: "LOAD_CSS_CLASSES",
            classNames
        } as LoadCssClassesClientToServerMessage));
    }
}

export function sendStorylineStateToVsCode() {
    return async (_dispatch, getState: () => RootState) => {
        if (connection?.readyState === WebSocket.OPEN) {
            const storyline = getState()?.storyline;
            if (!storyline?.currentParagraph) return;

            connection.send(JSON.stringify({
                type: "STORYLINE_LOADED",
                storyline: {
                    name: storyline.name,
                    currentPage: {
                        name: storyline.currentParagraph.pageName,
                        template: storyline.currentParagraph.templateName
                    },
                    version: "2"
                }
            } as StorylineLoadedClientToServerMessage));
        }
    }
}

export function sendPageStateToVsCode() {
    return async (_dispatch, getState: () => RootState) => {
        if (connection?.readyState === WebSocket.OPEN) {
            const storyline = getState()?.storyline;
            if (!storyline?.currentParagraph) return;


            connection.send(JSON.stringify({
                type: "PAGE_LOADED",
                page: {
                    name: storyline.currentParagraph.pageName,
                    template: storyline.currentParagraph.templateName
                }
            } as PageLoadedClientToServerMessage));
        }
    }
}

export function sendTemplateBindingsToVsCode(path: string, inputs: Record<string, any>) {
    if (connection?.readyState === WebSocket.OPEN) {
        connection.send(JSON.stringify({
            type: "TEMPLATE_RENDERED",
            name: path,
            inputs
        } as TemplateRenderedClientToServerMessage));
    }
}
